import type { BillingStatus, PlanName, SubscriptionCreateResponse } from '../../../api/types';

// PARA-131 (AD-5) — чистые функции интерпретации ответов биллинга.
// Единственное место, где решается «какое состояние показать» и «оплата /
// уже выполнено / нарушение контракта». Тестируется без рендера и таймеров.

/** Пять состояний AC-13. */
export type BillingUiState =
  /** 1. Активна и продлится (auto_renew = true). */
  | 'active-renewing'
  /** 2. Отменена, доступ до даты — доступно возобновление (AC-13c). */
  | 'cancelled-with-access'
  /** 3. Есть неоплаченный счёт (PENDING при действующем платном плане). */
  | 'pending-invoice'
  /** 4. Действует, но не продлится — legacy stub PARA-119 (Р-10). */
  | 'active-not-renewing'
  /** 5. Бесплатный тариф. */
  | 'free';

export function resolveBillingUiState(status: BillingStatus): BillingUiState {
  if (status.plan === 'FREE') {
    // subscription_status здесь любое, включая null («подписки не было») —
    // это не ошибка и не деградация (Решение Р-11).
    return 'free';
  }
  if (status.auto_renew) {
    return 'active-renewing';
  }
  if (status.subscription_status === 'CANCELLED') {
    return 'cancelled-with-access';
  }
  if (status.subscription_status === 'PENDING') {
    return 'pending-invoice';
  }
  // ACTIVE без auto_renew — legacy stub. Сюда же попадают EXPIRED / null при
  // ещё не понижённом плане (часовой downgrade-job PARA-119): показать «доступ
  // до <дата>» без кнопок — единственное, что не солжёт пользователю.
  return 'active-not-renewing';
}

/** AC-13b: неоплаченный счёт существует в любом из пяти состояний. */
export function hasPendingInvoice(status: BillingStatus): boolean {
  return status.subscription_status === 'PENDING';
}

/** Исход POST /billing/subscriptions (AC-5, Решение Р-7). */
export type SubscriptionOutcome = 'checkout' | 'already-active' | 'contract-violation';

/** Ветвление ТОЛЬКО по полю `status`. Проверять пустоту `checkout_url` как
 * признак ошибки запрещено: null — штатный ответ AC-24/AC-25 PARA-130. */
export function resolveSubscriptionOutcome(
  response: SubscriptionCreateResponse,
): SubscriptionOutcome {
  const hasUrl = typeof response.checkout_url === 'string' && response.checkout_url.length > 0;
  if (response.status === 'PENDING') {
    return hasUrl ? 'checkout' : 'contract-violation';
  }
  if (response.status === 'ACTIVE') {
    return hasUrl ? 'contract-violation' : 'already-active';
  }
  return 'contract-violation';
}

/** Локализованная дата окончания периода (AC-2: не ISO-строка). */
export function formatBillingDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
}

// PARA-140 (AC-4e) — валюта Telegram Stars: сумма приходит целым числом
// звёзд, minor units у неё нет. Все остальные валюты backend отдаёт в minor
// units (копейки/центы), поэтому делятся на 100.
const STARS_CURRENCY = 'XTR';
const MINOR_UNITS_IN_MAJOR = 100;

/** Сумма транзакции для истории и квитанции (AC-4e): `150 XTR` для Stars,
 * `4.99 USD` для остальных валют. Backend отдаёт `amount` «как есть». */
export function formatTransactionAmount(amount: number, currency: string, locale: string): string {
  const isStars = currency === STARS_CURRENCY;
  const value = isStars ? amount : amount / MINOR_UNITS_IN_MAJOR;
  const fractionDigits = isStars ? 0 : 2;
  const formatted = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
  return `${formatted} ${currency}`;
}

/** Порядок тарифов — нужен только чтобы отличить понижение от повышения (AC-14b). */
const PLAN_RANK: Record<PlanName, number> = { FREE: 0, PRO: 1, ULTIMATE: 2 };

export function isDowngrade(from: PlanName, to: PlanName): boolean {
  return PLAN_RANK[to] < PLAN_RANK[from];
}
